import { createAsyncThunk } from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "@store/store-helper";
import { FetchCreditTransactionThunkProps } from "@store/credit-transactions/credit-transactions-slice-types";
import { fetchProjectsByIds } from "@store/projects/projects-slice-thunk";
import { ActivityApiPaginatedResponse } from "@api/activity-api/activity-api-types";
import { ProjectId } from "@custom-types/base-types";

/**
 * Fetches credit transactions from backend so they can be put into the store
 * @throws {Error} if the Activity API client request fails
 */
export const fetchCreditTransactions = createAsyncThunk<
  ActivityApiPaginatedResponse,
  FetchCreditTransactionThunkProps,
  { state: RootState; dispatch: AppDispatch }
>(
  "creditTransactions/fetchCreditTransactions",
  async (
    { activityApiClient, after, pageSize },
    { getState, dispatch }
  ) => {
    const {
      projects: { ids: projectIds },
    } = getState();

    const response = await activityApiClient.fetchCreditTransaction({
      after,
      pageSize,
    });

    const projectIdsInStore = new Set(projectIds);

    /** Distinct array of project ids those need to fetch from backend */
    const projectIdsToFetch = new Set<ProjectId>();
    response.data.forEach((transaction) => {
      const projectId = transaction.context.projectId;
      if (projectId && !projectIdsInStore.has(projectId)) {
        projectIdsToFetch.add(projectId);
      }
    });

    // Fetch projects that are not available in store
    if (projectIdsToFetch.size > 0) {
      void dispatch(
        fetchProjectsByIds({
          projectIds: [...projectIdsToFetch],
        })
      );
    }

    return response;
  }
);
